import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PixelRatio, StyleSheet } from "react-native";

import type { ApartmentType } from "@stepapp/core/apiTypes";
import { Block, IconButton, Touchable, Typography, palette, spacing } from "@stepapp/ui";
import { useRouter } from "expo-router";

type ParsedProperty = {
  city: string;
  doorCode: string;
  floorArea: number;
  homeType: ApartmentType;
  isDefault: boolean;
  name: string;
  notes: string;
  postalOrZipCode: string;
  propertyId: string;
  streetAddress1: string;
  streetAddress2: string;
};

type Props = {
  property?: ParsedProperty;
  isHomeEditScreen?: boolean;
};

const HomeSection = ({ property, isHomeEditScreen }: Props) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();

  const navigateToEditProperty = useCallback(() => {
    navigate({
      pathname: "/my-home/edit",
      params: { propertyId: property?.propertyId },
    });
  }, [navigate, property?.propertyId]);

  return (
    <Block flex={1} style={styles.container} testID="property">
      <Touchable onPress={navigateToEditProperty} style={styles.containerTouchable}>
        <Block justifySpace row>
          <Typography style={styles.propertyName} variant="newH2">
            {property?.name}
          </Typography>

          {isHomeEditScreen && (
            <IconButton
              size={spacing.unit * 4.5}
              name="edit"
              style={styles.editContainer}
              iconSize={spacing.unit * 2}
              iconColor={palette.common.black}
            />
          )}
        </Block>
        <Block paddingTop={1}>
          <Typography decoration="capitalize">
            {`${t("screens.myHome.st")}. ${property?.streetAddress1}`}
          </Typography>

          <Block height={3}>
            <Typography decoration="capitalize">{property?.streetAddress2}</Typography>
          </Block>
        </Block>

        <Typography>
          {property?.postalOrZipCode} {property?.city}
        </Typography>

        <Block alignCenter row marginTop={1}>
          <Typography>{t("screens.homeEdit.type", { context: property?.homeType })}</Typography>
          <Block style={styles.dot} />
          <Typography>{`${property?.floorArea}m`}</Typography>
          <Typography style={styles.square} variant="smallParagraph">
            {2}
          </Typography>
        </Block>
      </Touchable>
    </Block>
  );
};

export default React.memo(HomeSection);

const styles = StyleSheet.create({
  container: {
    borderColor: palette.common.mediumGray,
    borderRadius: spacing.radius * 1,
    borderWidth: PixelRatio.roundToNearestPixel(0.5),
    boxShadow: "4px 4px 15px 0px rgba(0, 0, 0, 0.10)",
    justifyContent: "flex-end",
    marginTop: spacing.unit * 3,
  },
  containerTouchable: {
    paddingHorizontal: spacing.unit * 3,
    paddingVertical: spacing.unit * 2.5,
  },
  dot: {
    backgroundColor: palette.common.black,
    borderRadius: spacing.unit * 1,
    height: spacing.unit * 0.375,
    marginBottom: spacing.unit * 0.5,
    marginHorizontal: spacing.unit * 1,
    width: spacing.unit * 0.375,
  },
  editContainer: {
    marginRight: spacing.unit * -1,
  },
  propertyName: {
    flexWrap: "wrap",
    flex: 1,
  },
  square: {
    marginBottom: spacing.unit * 1,
  },
});
